import React from 'react';
import { Info, Plus, Trash2 } from 'lucide-react';
import type { ChatWidget } from './types';

interface ChatWidgetsTabProps {
  chatWidgets: ChatWidget[];
  onAdd: () => void;
  onChange: (index: number, field: string, value: any) => void;
  onRemove: (index: number) => void;
}

const IntercomConfig: React.FC<{
  widget: ChatWidget;
  index: number;
  onChange: ChatWidgetsTabProps['onChange'];
}> = ({ widget, index, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Intercom App ID *</label>
    <input
      type="text"
      value={widget.config.app_id || ''}
      onChange={e => onChange(index, 'config.app_id', e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      placeholder="abcd1234"
    />
  </div>
);

const DriftConfig: React.FC<{
  widget: ChatWidget;
  index: number;
  onChange: ChatWidgetsTabProps['onChange'];
}> = ({ widget, index, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Drift Embed ID *</label>
    <input
      type="text"
      value={widget.config.embed_id || ''}
      onChange={e => onChange(index, 'config.embed_id', e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      placeholder="abcd1234"
    />
  </div>
);

const CrispConfig: React.FC<{
  widget: ChatWidget;
  index: number;
  onChange: ChatWidgetsTabProps['onChange'];
}> = ({ widget, index, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Crisp Website ID *</label>
    <input
      type="text"
      value={widget.config.website_id || ''}
      onChange={e => onChange(index, 'config.website_id', e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      placeholder="abcd1234-efgh-5678-ijkl-mnop9012qrst"
    />
  </div>
);

const TawkConfig: React.FC<{
  widget: ChatWidget;
  index: number;
  onChange: ChatWidgetsTabProps['onChange'];
}> = ({ widget, index, onChange }) => (
  <div className="space-y-3">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Tawk.to Property ID *</label>
      <input
        type="text"
        value={widget.config.property_id || ''}
        onChange={e => onChange(index, 'config.property_id', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="5f1234567890abcdef123456"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Tawk.to Widget ID *</label>
      <input
        type="text"
        value={widget.config.widget_id || ''}
        onChange={e => onChange(index, 'config.widget_id', e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="default"
      />
    </div>
  </div>
);

const CustomConfig: React.FC<{
  widget: ChatWidget;
  index: number;
  onChange: ChatWidgetsTabProps['onChange'];
}> = ({ widget, index, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Chat Script *</label>
    <textarea
      value={widget.config.script || ''}
      onChange={e => onChange(index, 'config.script', e.target.value)}
      rows={6}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
      placeholder="// Your custom chat widget code here"
    />
    <p className="mt-1 text-xs text-gray-500">Do not include &lt;script&gt; tags</p>
  </div>
);

export const ChatWidgetsTab: React.FC<ChatWidgetsTabProps> = ({
  chatWidgets,
  onAdd,
  onChange,
  onRemove,
}) => (
  <div className="space-y-4">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-start space-x-2">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Chat Widgets</p>
          <p>
            Integrate popular chat widgets like Intercom, Drift, Crisp, or Tawk.to. You can also add
            custom chat widget scripts.
          </p>
        </div>
      </div>
    </div>

    <div className="space-y-3">
      {chatWidgets.map((widget, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">Chat Widget {index + 1}</h4>
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 size={16} />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
            <select
              value={widget.provider}
              onChange={e => onChange(index, 'provider', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="intercom">Intercom</option>
              <option value="drift">Drift</option>
              <option value="crisp">Crisp</option>
              <option value="tawk">Tawk.to</option>
              <option value="custom">Custom Script</option>
            </select>
          </div>

          {widget.provider === 'intercom' && (
            <IntercomConfig widget={widget} index={index} onChange={onChange} />
          )}
          {widget.provider === 'drift' && (
            <DriftConfig widget={widget} index={index} onChange={onChange} />
          )}
          {widget.provider === 'crisp' && (
            <CrispConfig widget={widget} index={index} onChange={onChange} />
          )}
          {widget.provider === 'tawk' && (
            <TawkConfig widget={widget} index={index} onChange={onChange} />
          )}
          {widget.provider === 'custom' && (
            <CustomConfig widget={widget} index={index} onChange={onChange} />
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={onAdd}
        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
      >
        <Plus size={16} />
        <span>Add Chat Widget</span>
      </button>
    </div>
  </div>
);
